import { type RemovableRef, useLocalStorage } from '@vueuse/core'
import { useConfigStore } from '@/stores/Config.store.ts'
import { RoleHelper } from '@/helpers/RoleHelper.ts'
import { useAuthStore, WhoAmI } from '@/stores'
import { computed, type Ref } from 'vue'

export interface DailyWorkHoursDefinition {
  /** @description Hours at Sunday */
  0: number
  /** @description Hours at Monday */
  1: number
  /** @description Hours at Tuesday */
  2: number
  /** @description Hours at Wednesday */
  3: number
  /** @description Hours at Thursday */
  4: number
  /** @description Hours at Friday */
  5: number
  /** @description Hours at Saturday */
  6: number
}

export interface TitropGlobalUser {
  /** @description Current user */
  current: WhoAmI
  /** @description Roles of current user*/
  roles: RoleHelper
  /** @description Shortcut for current.id */
  currentIri: string
  // readonly currentIri: ComputedRef<string>
  /** @description Shortcut for current.@id */
  currentId: number
  // readonly currentId: ComputedRef<number>

  /** @description Shortcut user configuration for daily working hours */
  daily_workhours: () => DailyWorkHoursDefinition
}

export interface TitropGlobal {
  /** @description Is DEV environment? */
  DEV: boolean

  /** @description Is PROD environment? */
  PROD: boolean

  /** @description Is STAGING environment? */
  STAGING: boolean

  /** @description Which mode the application is in? */
  MODE: HintedString<'production' | 'development' | 'staging'>

  /** @description Enable <DevDump> component?*/
  enableDevDumps: Ref<boolean>
  /** @description Enable the extended view */
  extendedView: Ref<boolean>

  /** @description Shows the filename in router views */
  showViewComponents: Ref<boolean>

  /** @description Shows ids in specific places */
  showIds: Ref<boolean>
  /** @description Information abount current logged in user*/
  user: TitropGlobalUser
  /** @description Provides access to application and user configuration */
  config: {
    /** @description Gets a ref to an application wide config (read-only!) - ALWAYS use '.value'! */
    app: <T>(key: string, defaultVal?: T) => Readonly<Ref<T | undefined>>

    /** @description Gets a writable ref to users config -  - ALWAYS use '.value'!*/
    user: <T>(key: string, defaultValue: T) => RemovableRef<T>

    /** @description Forces a write to users config */
    userSet: <T>(key: string, value: T) => void

    /** @description Gets a writable ref to users config - auto-add router path - ALWAYS use '.value'!*/
    userPathed: <T>(key: string, defaultValue: T) => RemovableRef<T>
  }
}

const currentEmpReceived = ref(false)
const currentEmpConfigReceived = ref(false)
const isFullyReady = computed(() => currentEmpConfigReceived.value && currentEmpReceived.value && useTitrop().value.user.currentIri)
const readyEvent = useTitropEvents().onTitropReady
const unreadyEvent = useTitropEvents().onTitropUnready
watch(
  isFullyReady,
  (newVal, oldVal) => {
    if (!oldVal && newVal) {
      readyEvent.emit()
      console.log('Titrop ready')
      // watchHandle.stop()
    } else if (oldVal && !newVal) {
      unreadyEvent.emit()
      console.log('Titrop unready')
      // watchHandle.stop()
    }
  },
  { once: false, immediate: true }
)

export function whenTitropReady(listener: () => void) {
  if (isFullyReady.value) listener()
  else readyEvent.once(listener)
}

export function whenTitropUnready(listener: () => void) {
  if (!isFullyReady.value) listener()
  else unreadyEvent.once(listener)
}

let titropGlobal!: Ref<TitropGlobal>

// const extendedView = computed({
//   get: () => useLocalStorage('show-extended-view', false).value,
//   set: (newValue) => (useLocalStorage('show-extended-view', false).value = newValue)
// })
//
// const showIds = computed({
//   get: () => useLocalStorage('show-ids', false).value,
//   set: (newValue) => (useLocalStorage('show-ids', false).value = newValue)
// })
//
// const enableDevDumps2 = computed({
//   get: () => useLocalStorage('show-dev-dumps', false, { listenToStorageChanges: true }).value,
//   set: (newValue) => (useLocalStorage('show-dev-dumps', false).value = newValue)
// })
// const showViewComponents = computed({
//   get: () => useLocalStorage('show-view-names', false).value,
//   set: (newValue) => (useLocalStorage('show-view-names', false).value = newValue)
// })
//

const extendedView = useLocalStorage('show-extended-view', false)
const showIds = useLocalStorage('show-ids', false)
const enableDevDumps = useLocalStorage('show-dev-dumps', false, { listenToStorageChanges: true })
const showViewComponents = useLocalStorage('show-view-names', false)

const STAGING = import.meta.env.MODE == 'staging'
const { DEV } = import.meta.env
const { PROD } = import.meta.env
const { MODE } = import.meta.env

const dailyWorkHoursFunc = (): DailyWorkHoursDefinition =>
  useConfigStore().get<DailyWorkHoursDefinition>('daily_work_hours', {
    0: 0,
    1: 8,
    2: 8,
    3: 8,
    4: 8,
    5: 8,
    6: 0
  }).value
const fnConfigApp = <T>(key: string, defaultValue?: T): Readonly<Ref<T | undefined>> => useConfigStore().appGet(key, defaultValue)
const fnConfigUser = <T>(key: string, defaultValue: T): RemovableRef<T> => useConfigStore().get(key, defaultValue)
const fnConfigUserSet = <T>(key: string, value: T): void => useConfigStore().set(key, value)

export function useTitrop(): Ref<TitropGlobal> {
  if (titropGlobal) {
    // console.warn('TITG -> access')
    return titropGlobal
  }
  // console.warn('TITG -> init')
  // const currentUser = undefined as WhoAmI
  const roleHelper: RoleHelper = new RoleHelper()

  titropGlobal = ref<TitropGlobal>({
    DEV: DEV,
    PROD: PROD,
    STAGING: STAGING,
    MODE: MODE,
    enableDevDumps: enableDevDumps,
    extendedView: extendedView,
    showIds: showIds,
    showViewComponents: showViewComponents,
    user: {
      current: undefined as unknown as WhoAmI, //little hack for correct typing
      roles: roleHelper,
      currentIri: '',
      currentId: -1,
      daily_workhours: dailyWorkHoursFunc
    },
    config: {
      app: fnConfigApp,
      user: fnConfigUser,
      userSet: fnConfigUserSet,
      userPathed: <T>(key: string, defaultValue: T): RemovableRef<T> => {
        return useConfigStore().getPathed(key, defaultValue)
      }
    }
  }) as unknown as Ref<TitropGlobal>
  watch(titropGlobal.value.user, (user) => {
    if (!user?.current?.['@id']) {
      //error case in hot-reload case
      void useAuthStore()
        .refreshWhoAmI()
        .then((wai) => (titropGlobal.value.user.current = wai))
      return
    }
    titropGlobal.value.user.currentIri = user.current['@id']
    titropGlobal.value.user.currentId = user.current.id
    currentEmpReceived.value = true
  })
  useConfigStore().$subscribe(
    (mutation, state) => {
      if (mutation.type === 'direct') {
        // Einzelne Property-Zuweisung
        if (mutation.events?.key === 'ready' && currentEmpConfigReceived.value !== mutation.events?.newValue) {
          currentEmpConfigReceived.value = state.ready
        }
      } else if (mutation.type.startsWith('patch')) {
        // Fallback: prüfe direkt den State
        if (state.ready === true && currentEmpConfigReceived.value !== state.ready) {
          currentEmpConfigReceived.value = state.ready
        }
      }
    },
    { deep: true }
  )

  // const authStore = useAuthStore()
  // authStore.$subscribe((mutation, state) => {
  //   if (!titropGlobal) return
  //   // if (state.whoami) titropGlobal.user.current = state.whoami
  //   // if (state.roles) titropGlobal.user.roles = state.roles
  //
  // })

  // if (authStore.$state.whoami) titropGlobal.user.current = authStore.$state.whoami
  // if (authStore.$state.roles) titropGlobal.user.roles = authStore.$state.roles
  // if (import.meta.env.DEV) window.__TITROP__.$titrop = titropGlobal
  return titropGlobal
}
